import {
    normalizeOrderTaskPositionAssigneeType,
    OrderTaskPositionAssigneeType,
} from '@shared/helpers/order-task-position-assignee.util';
import { BcmEmployee, BcmEmployeeDto } from '@shared/models/bcm-employee';
import { BcmDepartment, BcmDepartmentDto } from '@shared/models/bcm-department';
import { User } from '@core/services/api/u2b-user.api-service';
import { BcmOrder } from '@shared/models/bcm-order';

export interface OrderAssigneeFormValue {
    assigneeType: OrderTaskPositionAssigneeType | null;
    bcm_employees_id: number | null;
    bcm_departments_id: number | null;
}

export interface OrderAssigneeDisplaySnapshot {
    assigneeType?: OrderTaskPositionAssigneeType | string | null;
    bcm_employees_id?: number | null;
    bcm_departments_id?: number | null;
    assignedEmployee?: BcmEmployeeDto | BcmEmployee | null;
    assignedDepartment?: BcmDepartmentDto | BcmDepartment | null;
    assignedUserObject?: User | null;
}

export function resolveOrderAssigneeDisplayName(snapshot: OrderAssigneeDisplaySnapshot): string {
    const assigneeType = normalizeOrderTaskPositionAssigneeType(snapshot.assigneeType);

    if (assigneeType === OrderTaskPositionAssigneeType.Department) {
        const department = snapshot.assignedDepartment;

        if (department?.name) {
            return department.name.trim();
        }
    }

    if (assigneeType === OrderTaskPositionAssigneeType.Employee) {
        const employee = snapshot.assignedEmployee instanceof BcmEmployee
            ? snapshot.assignedEmployee
            : snapshot.assignedEmployee
                ? new BcmEmployee(snapshot.assignedEmployee)
                : null;

        if (employee) {
            const profile = employee.profile;
            const profileName = profile
                ? [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim()
                : '';

            if (profileName) {
                return profileName;
            }
        }

        const user = snapshot.assignedUserObject;
        const userName = user
            ? [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
            : '';

        if (userName) {
            return userName;
        }

        if (employee?.employeeNumber) {
            return employee.employeeNumber.trim();
        }
    }

    const user = snapshot.assignedUserObject;

    if (user) {
        const legacyName = [user.lastName?.trim(), ', ', user.firstName?.trim()].join('').trim();

        if (legacyName && legacyName !== ',') {
            return legacyName;
        }

        return [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    }

    return '';
}

export function resolveOrderAssigneeFormDefaults(
    snapshot: OrderAssigneeDisplaySnapshot,
    employees: BcmEmployee[] = [],
): OrderAssigneeFormValue {
    const assigneeType = normalizeOrderTaskPositionAssigneeType(snapshot.assigneeType);

    if (assigneeType) {
        return {
            assigneeType,
            bcm_employees_id: snapshot.bcm_employees_id ?? null,
            bcm_departments_id: snapshot.bcm_departments_id ?? null,
        };
    }

    if (snapshot.assignedUserObject?.id && employees.length) {
        const employee = employees.find(item => item.userId === snapshot.assignedUserObject?.id);

        if (employee?.id) {
            return {
                assigneeType: OrderTaskPositionAssigneeType.Employee,
                bcm_employees_id: employee.id,
                bcm_departments_id: null,
            };
        }
    }

    return {
        assigneeType: null,
        bcm_employees_id: null,
        bcm_departments_id: null,
    };
}

export function isOrderAssigneeFormValueComplete(value: OrderAssigneeFormValue): boolean {
    const assigneeType = normalizeOrderTaskPositionAssigneeType(value.assigneeType);

    if (assigneeType === OrderTaskPositionAssigneeType.Employee) {
        return value.bcm_employees_id != null;
    }

    if (assigneeType === OrderTaskPositionAssigneeType.Department) {
        return value.bcm_departments_id != null;
    }

    return false;
}

export function mapOrderAssigneeFormValueToPayload(
    value: OrderAssigneeFormValue,
): OrderAssigneeFormValue & { assignedUser?: string | null } {
    if (!value.assigneeType) {
        return {
            assigneeType: null,
            bcm_employees_id: null,
            bcm_departments_id: null,
        };
    }

    if (value.assigneeType === OrderTaskPositionAssigneeType.Employee) {
        return {
            assigneeType: value.assigneeType,
            bcm_employees_id: value.bcm_employees_id ?? null,
            bcm_departments_id: null,
        };
    }

    if (value.assigneeType === OrderTaskPositionAssigneeType.Department) {
        return {
            assigneeType: value.assigneeType,
            bcm_employees_id: null,
            bcm_departments_id: value.bcm_departments_id ?? null,
        };
    }

    return {
        assigneeType: null,
        bcm_employees_id: null,
        bcm_departments_id: null,
    };
}

export function buildOrderAssigneeUpdateFromSelectedUser(
    selectedUserId: string | null | undefined,
    employees: BcmEmployee[] = [],
): (OrderAssigneeFormValue & { assignedUser?: string | null }) | null {
    if (!selectedUserId) {
        return null;
    }

    const employee = employees.find((item) => item.userId === selectedUserId);

    if (employee?.id) {
        return mapOrderAssigneeFormValueToPayload({
            assigneeType: OrderTaskPositionAssigneeType.Employee,
            bcm_employees_id: employee.id,
            bcm_departments_id: null,
        });
    }

    return {
        assigneeType: null,
        bcm_employees_id: null,
        bcm_departments_id: null,
        assignedUser: selectedUserId,
    };
}

export function applyOrderAssigneeFieldsToOrder(target: BcmOrder, source: BcmOrder): void {
    target.assigneeType = source.assigneeType ?? null;
    target.bcm_employees_id = source.bcm_employees_id ?? null;
    target.bcm_departments_id = source.bcm_departments_id ?? null;
    target.assignedUser = source.assignedUser ?? null;
    target.assignedEmployee = source.assignedEmployee ?? null;
    target.assignedDepartment = source.assignedDepartment ?? null;
    target.assignedUserObject = source.assignedUserObject ?? null;
    target.assignedUserName = source.assignedUserName ?? resolveOrderAssigneeDisplayName(source);
}

export function resolveOrderEmployeeLabel(employee: BcmEmployee): string {
    const profile = employee.profile;

    if (!profile) {
        return employee.employeeNumber || `#${employee.id}`;
    }

    const name = [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim();

    return name || employee.employeeNumber || `#${employee.id}`;
}

export function resolveOrderEmployeePrimaryDepartmentName(
    employee: BcmEmployee,
    departments?: readonly BcmDepartment[],
): string | null {
    const fromEmployee = employee.primaryDepartment?.name?.trim();

    if (fromEmployee) {
        return fromEmployee;
    }

    const primaryDepartmentId = employee.primaryDepartmentId;

    if (primaryDepartmentId == null || !departments?.length) {
        return null;
    }

    return departments.find((department) => department.id === primaryDepartmentId)?.name?.trim() ?? null;
}

/** Mitarbeiter-Auswahl (Auftrag/Aufgabe): Name plus Hauptabteilung, falls vorhanden. */
export function resolveOrderEmployeeAssigneeOptionLabel(
    employee: BcmEmployee,
    departments?: readonly BcmDepartment[],
): string {
    const name = resolveOrderEmployeeLabel(employee);
    const departmentName = resolveOrderEmployeePrimaryDepartmentName(employee, departments);

    if (!departmentName) {
        return name;
    }

    return `${name} · ${departmentName}`;
}
